import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { type AppState, type BaseAppState, type EntityRef, Status } from "@client/types";
import { isStaffOrSuperUser } from "@common/appInfo";
import toasts from "@common/lib/toast";
import { type AnyAction, type Dispatch } from "redux";
import { DUMMY_ORDER, isDummyOrder } from "../OrderConfirmation/lib";
import { type OrderConfirmation } from "../OrderConfirmation/types";
import { initAction } from "../actions";
import api from "./api";
import { previewState } from "./reducer";
import { localStateSelector } from "./selectors";
import { type MemberOrders } from "./types";

export enum Actions {
  InitBegin = "MEMBER_ORDERS_INIT_BEGIN",
  InitFailure = "MEMBER_ORDERS_INIT_FAILURE",
  InitSuccess = "MEMBER_ORDERS_INIT_SUCCESS",

  GetDetailBegin = "MEMBER_ORDERS_GET_DETAIL_BEGIN",
  GetDetailFailure = "MEMBER_ORDERS_GET_DETAIL_FAILURE",
  GetDetailSuccess = "MEMBER_ORDERS_GET_DETAIL_SUCCESS",

  SetShowingDetailId = "MEMBER_ORDERS_SET_SHOWING_DETAIL_ID_BEGIN",

  CreateCommentBegin = "MEMBER_ORDERS_CREATE_COMMENT_BEGIN",
  CreateCommentFailure = "MEMBER_ORDERS_CREATE_COMMENT_FAILURE",
  CreateCommentSuccess = "MEMBER_ORDERS_CREATE_COMMENT_SUCCESS",

  SetAddCommentModalVisible = "MEMBER_ORDERS_ADD_COMMENT_MODAL"
}

export type InitBeginActionData = EntityRef;
export interface InitSuccessActionData {
  items: MemberOrders.Item[];
}
export const _init = initAction<
  BaseAppState,
  InitBeginActionData,
  MemberOrders.Api.InitResponse,
  InitSuccessActionData
>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: BaseAppState, dispatch: any, args: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    return api.init(apiInfo, {});
  },
  mapResponse: (state: BaseAppState, resp: MemberOrders.Api.InitResponse) => {
    const res = {
      items: resp.items
    };

    if (!res.items.length && isStaffOrSuperUser()) {
      res.items = previewState.items;
    }

    return res;
  }
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (localState.status === Status.None) {
    dispatch(_init(args));
  }
};

export type GetDetailBeginActionData = EntityRef & {
  itemId: string;
};
export type GetDetailSuccessActionData = MemberOrders.ItemDetail;
export const _getDetail = apiAction<
  BaseAppState,
  GetDetailBeginActionData,
  MemberOrders.Api.GetDetailResponse,
  GetDetailSuccessActionData
>({
  types: {
    begin: Actions.GetDetailBegin,
    failure: Actions.GetDetailFailure,
    success: Actions.GetDetailSuccess
  },
  action: (state: BaseAppState, dispatch: any, params: GetDetailBeginActionData) => {
    if (isDummyOrder(params.itemId)) {
      return Promise.resolve({
        status: 200,
        response: {
          data: DUMMY_ORDER,
          status: "success"
        }
      });
    }

    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.detail(apiInfo, { id: params.itemId });
  }
});

export type GetDetailArgs = EntityRef & {
  itemId: string;
};
export const getDetail = (args: GetDetailArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (!(args.itemId in localState.detail)) {
    dispatch(_getDetail(args));
  }
};

export type SetShowingDetailIdActionData = EntityRef & {
  itemId: string | null;
};
export const setShowingDetailId = (args: SetShowingDetailIdActionData) => (dispatch: Dispatch<any>) => {
  if (args.itemId) dispatch(getDetail({ id: args.id, source: args.source, itemId: args.itemId }));

  dispatch({
    type: Actions.SetShowingDetailId,
    data: <SetShowingDetailIdActionData>args
  });
};

export type CreateCommentBeginActionData = EntityRef & {
  comment: string;
};
export type CreateCommentSuccessActionData = OrderConfirmation.Api.CreateCommentResponse;
export const _createComment = apiAction<AppState, CreateCommentBeginActionData, CreateCommentSuccessActionData>({
  types: {
    begin: Actions.CreateCommentBegin,
    failure: Actions.CreateCommentFailure,
    success: [
      Actions.CreateCommentSuccess,
      ({ dispatch, params }) => dispatch(hideAddCommentModal(params)),
      () => {
        toasts.submitted();
      }
    ]
  },
  action: (state: AppState, dispatch: Dispatch<AnyAction>, args: CreateCommentBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    const localState = localStateSelector(state, args);

    return api.createComment(apiInfo, localState.showingDetailId!, args.comment);
  }
});

export type CreateCommentArgs = EntityRef & {
  comment: string;
};
export const createComment = (args: CreateCommentArgs) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);

  if (localState.status !== "Creating") {
    dispatch(_createComment(args));
  }
};

export type SetAddCommentModalVisibleActionData = EntityRef & {
  isVisible: boolean;
};
export const hideAddCommentModal = (ref: EntityRef) => ({
  type: Actions.SetAddCommentModalVisible,
  data: <SetAddCommentModalVisibleActionData>{ ...ref, isVisible: false }
});
export const showAddCommentModal = (ref: EntityRef) => ({
  type: Actions.SetAddCommentModalVisible,
  data: <SetAddCommentModalVisibleActionData>{ ...ref, isVisible: true }
});
